import Api from '@/utils/api'
import BasicConfig from '@/config/basic'

/** GET /monitoringdevice/list — 萤石设备列表；筛选 query 由调用页传入（如 storeId） */
export function getYingshiDeviceList (query = {}) {
  return Api.get(`${BasicConfig.jDomain}/monitoringdevice/list`, {
    params: {
      includeExtendedFields: true,
      ...query,
      axiosType: 'java'
    }
  })
}

/** 更新监控设备信息 POST /monitoringdevice/{id}/with-image（multipart） */
export function updateYingshiDevice (id, body = {}) {
  return Api.post(`${BasicConfig.jDomain}/monitoringdevice/${id}/with-image`, body, {
    axiosType: 'javaMultipart'
  })
}

/** 设置设备是否需要推理 PUT /monitoringdevice/{id}/needInference */
export function updateYingshiDeviceNeedInference (id, body = {}) {
  return Api.put(
    `${BasicConfig.jDomain}/monitoringdevice/${id}/needInference`,
    {},
    { params: { ...body, axiosType: 'java' } }
  )
}

/** 批量指定设备所属店铺 PUT /monitoringdevice/batch-assign-store（JSON） */
export function batchAssignYingshiDeviceStore (body = {}) {
  return Api.put(`${BasicConfig.jDomain}/monitoringdevice/batch-assign-store`, body, {
    axiosType: 'javaJson'
  })
}

/** 从萤石云同步直连设备 POST /monitoringdevice/yingshi/sync/direct */
export function syncYingshiDevices (body = {}) {
  return Api.post(`${BasicConfig.jDomain}/monitoringdevice/yingshi/sync/direct`, body, {
    axiosType: 'java'
  })
}

/** 从萤石云同步分享设备 POST /monitoringdevice/yingshi/sync */
export function syncYingshiShareDevices (body = {}) {
  return Api.post(`${BasicConfig.jDomain}/monitoringdevice/yingshi/sync`, body, {
    axiosType: 'java'
  })
}

/** 标记监控设备通道为错检 PUT /monitoringdevice/mark_false_positive */
export function markYingshiDeviceFalsePositive (body = {}) {
  const deviceId = body.deviceId == null ? '' : String(body.deviceId).trim()
  const channelNo = body.channelNo == null ? '' : String(body.channelNo).trim()
  if (!deviceId) {
    return Promise.reject(new Error('deviceId 不能为空'))
  }
  if (channelNo === '') {
    return Promise.reject(new Error('channelNo 不能为空'))
  }
  return Api.put(`${BasicConfig.jDomain}/monitoringdevice/mark_false_positive`, {
    deviceId,
    channelNo
  }, { axiosType: 'java' })
}

/**
 * 登记离室去向 POST /departurerecord（multipart）
 * body: deviceId, channelNo, nurseId, type, nurseAvatar；type=nursing 时 subType 必填
 */
export function createDepartureRecord (body = {}) {
  const deviceId = body.deviceId == null ? '' : String(body.deviceId).trim()
  const type = body.type == null ? '' : String(body.type).trim().toLowerCase()
  const subType = body.subType == null || body.subType === '' ? '' : String(body.subType).trim()
  const channelNo = Number(body.channelNo)
  const nurseId = Number(body.nurseId)
  const { nurseAvatar } = body
  if (!deviceId || body.channelNo == null || body.channelNo === '' || body.nurseId == null || body.nurseId === '' || !type) {
    return Promise.reject(new Error('deviceId、channelNo、nurseId 和 type 不能为空'))
  }
  if (type === 'nursing' && !subType) {
    return Promise.reject(new Error('护理操作须填写 subType'))
  }
  if (!nurseAvatar || typeof Blob === 'undefined' || !(nurseAvatar instanceof Blob)) {
    return Promise.reject(new Error('nurseAvatar 须为刷脸抓拍图片文件'))
  }
  if (!Number.isFinite(channelNo)) {
    return Promise.reject(new Error('channelNo 格式不正确'))
  }
  if (!Number.isFinite(nurseId)) {
    return Promise.reject(new Error('nurseId 格式不正确'))
  }
  const payload = { deviceId, channelNo, nurseId, type, nurseAvatar }
  if (type === 'nursing') {
    payload.subType = subType
  }
  return Api.post(`${BasicConfig.jDomain}/departurerecord`, payload, {
    axiosType: 'javaMultipart'
  })
}

/** 取消预登记 DELETE /departurerecord */
export function deleteDepartureRecord (query = {}) {
  const deviceId = query.deviceId == null ? '' : String(query.deviceId).trim()
  const channelNo = Number(query.channelNo)
  const nurseId = Number(query.nurseId)
  if (!deviceId || query.channelNo == null || query.channelNo === '' || query.nurseId == null || query.nurseId === '') {
    return Promise.reject(new Error('deviceId、channelNo 和 nurseId 不能为空'))
  }
  if (!Number.isFinite(channelNo)) {
    return Promise.reject(new Error('channelNo 格式不正确'))
  }
  if (!Number.isFinite(nurseId)) {
    return Promise.reject(new Error('nurseId 格式不正确'))
  }
  return Api.delete(`${BasicConfig.jDomain}/departurerecord`, {
    params: { deviceId, channelNo, nurseId, axiosType: 'java' }
  })
}

/**
 * 离室护理结束并登记下一步 POST /departurerecord/nursing/end（multipart）
 * body: deviceId, channelNo, nurseId, nextStep, nurseAvatar；nextStep=next_operation 时 nextSubType 必填
 */
export function endDepartureNursing (body = {}) {
  const deviceId = body.deviceId == null ? '' : String(body.deviceId).trim()
  const nextStep = body.nextStep == null ? '' : String(body.nextStep).trim()
  const nextSubType = body.nextSubType == null || body.nextSubType === ''
    ? ''
    : String(body.nextSubType).trim()
  const channelNo = Number(body.channelNo)
  const nurseId = Number(body.nurseId)
  const { nurseAvatar } = body
  if (!deviceId || body.channelNo == null || body.channelNo === '' || body.nurseId == null || body.nurseId === '' || !nextStep) {
    return Promise.reject(new Error('deviceId、channelNo、nurseId 和 nextStep 不能为空'))
  }
  if (nextStep === 'next_operation' && !nextSubType) {
    return Promise.reject(new Error('护理下一步须填写 nextSubType'))
  }
  if (!nurseAvatar || typeof Blob === 'undefined' || !(nurseAvatar instanceof Blob)) {
    return Promise.reject(new Error('nurseAvatar 须为刷脸抓拍图片文件'))
  }
  if (!Number.isFinite(channelNo)) {
    return Promise.reject(new Error('channelNo 格式不正确'))
  }
  if (!Number.isFinite(nurseId)) {
    return Promise.reject(new Error('nurseId 格式不正确'))
  }
  const payload = { deviceId, channelNo, nurseId, nextStep, nurseAvatar }
  if (nextStep === 'next_operation') {
    payload.nextSubType = nextSubType
  }
  return Api.post(`${BasicConfig.jDomain}/departurerecord/nursing/end`, payload, {
    axiosType: 'javaMultipart'
  })
}

/**
 * 确认送回监护室 POST /departurerecord/confirm-return（multipart）
 * 推理 hasBaby=true 时后端会自动确认；前端仅在人工确认（画面已见宝宝）时调用。
 */
export function confirmDepartureRecordReturn (body = {}) {
  const deviceId = body.deviceId == null ? '' : String(body.deviceId).trim()
  const channelNo = Number(body.channelNo)
  const nurseId = Number(body.nurseId)
  const { nurseAvatar } = body
  if (!deviceId || body.channelNo == null || body.channelNo === '' || body.nurseId == null || body.nurseId === '') {
    return Promise.reject(new Error('deviceId、channelNo 和 nurseId 不能为空'))
  }
  if (!nurseAvatar || typeof Blob === 'undefined' || !(nurseAvatar instanceof Blob)) {
    return Promise.reject(new Error('nurseAvatar 须为刷脸抓拍图片文件'))
  }
  if (!Number.isFinite(channelNo)) {
    return Promise.reject(new Error('channelNo 格式不正确'))
  }
  if (!Number.isFinite(nurseId)) {
    return Promise.reject(new Error('nurseId 格式不正确'))
  }
  return Api.post(`${BasicConfig.jDomain}/departurerecord/confirm-return`, {
    deviceId,
    channelNo,
    nurseId,
    nurseAvatar
  }, { axiosType: 'javaMultipart' })
}
